import React from 'react';
import AlarmConfigurationDialog from './AlarmConfigurationDialog';
import { sameRoutingSettings } from './routingSettings';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Stack,
  Checkbox,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { Delete, Assignment, Password, Devices, Edit, Visibility, VisibilityOff, PersonAdd, Dialpad, Phone, Apps, CloudDownload, CloudQueue, CloudOff, AddCircleOutline, Router } from '@mui/icons-material';
import * as ETVConstants from '../../ETVConstants';
import IntercomAssignmentDialog from './IntercomAssignmentDialog';
import IntercomDTMFDialog from './IntercomDTMFDialog';
import IntercomCallRoutingFields, { directRouting, routingError } from './IntercomCallRoutingFields';
import ETVPage from '../../tools/ETVPage';
import ETVPopup from '../../tools/ETVPopup';
import ETVMessagePopup from '../../tools/ETVMessagePopup';
import ETVActionButton from '../../tools/ETVActionButton';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import IntercomAlarmOverview from './IntercomAlarmOverview';
import IntercomAlarmTestOverview from './IntercomAlarmTestOverview';

class IntercomMgmt extends React.Component {
  state = {
    // data
    users: [],
    devices: [],
    all_devices: [],

    // loading/error
    isLoading: false,
    isLoaded: false,
    error: null,
    cloudSipBusyUserId: null,
    cloudSipPendingRemoval: null,
    cloudSipCredentials: null,

    // dialogs / info
    window: '',
    info_title: '',
    info_message: '',
    info_type: '',
    spinning: false,

    // add user
    newUserName: '',
    newFriendlyName:'',
    newDeviceId: '',
    addKind: 'user',
    addProvisionCloudSip: false,
    newDeviceSipSource: 'cloud',
    newPassword: '',
    newUserPrefix: 'app:',
    newSipExtension: '',
    newSipDomain: '',
    newSipBackupDomain: '',
    newSipTransport: 'udp',
    newSipBroadcastIp: '',
    newSipTestExtension: '',
    newEn8128AlarmWorkflowEnabled: false,
      newAlarmConfiguration: null,
    showNewSipPassword: false,
    addSaveAttempted: false,
    addSaving: false,

    // assignment dialog
    selected_device_id: '',

    // edit device SIP settings
    editDeviceUserId: null,
    editDeviceName: '',
    editDeviceCallerName: '',
    editDeviceCloudManaged: false,
    editSipExtension: '',
    editSipDomain: '',
    editSipBackupDomain: '',
    editSipPassword: '',
    editSipTransport: 'udp',
    editSipBroadcastIp: '',
    editSipTestExtension: '',
    editEn8128AlarmWorkflowEnabled: false,
    showEditSipPassword: false,
    editSipSaving: false,

    // edit intercom user
    editUserId: null,
    editUserCloudManaged: false,
    editUserPrefix: 'app:',
    editUserName: '',
    editFriendlyName: '',
    editUserPhone: '',
    editUserPassword: '',
    editUserSipExtension: '',
    editUserSipDomain: '',
    editUserSipPassword: '',
    editUserSipTransport: 'udp',
    showEditUserSipPassword: false,
    editUserSaving: false,

    // change password
    changePwUserId: null,
    changePwUserName: '',
    changePwNew: '',
    changePwConfirm: '',
    changePwSaving: false,

    // Grandstream import
    grandstreamIp: '',
    grandstreamContacts: [],
    grandstreamStep: 'address',
    grandstreamLoading: false,
    grandstreamSaving: false,
    grandstreamError: '',
  };

  componentDidMount() {
    this.loadIntercomUsers();
    this.loadAllDevices();
  }

  componentDidUpdate() {
    if (!this.state.isLoading && !this.state.isLoaded) {
      this.loadIntercomUsers();
    }
  }

  // --------------- Environment helpers ---------------
  getEnv() {
    const accessToken = localStorage.getItem('etv.access_token') || '';
    const reqUserID = localStorage.getItem('etv.user_id') || '';
    const baseRaw = ETVConstants.getServerBase?.() || '';
    const base = baseRaw.replace(/\/$/, '');
    return { accessToken, reqUserID, base };
  }

  failIfMixedContent(base) {
    if (
      typeof window !== 'undefined' &&
      window.location?.protocol === 'https:' &&
      base.startsWith('http:')
    ) {
      throw new Error(
        `Mixed content blocked: front-end is HTTPS but server base is HTTP (${base}). Use HTTPS or a same-origin proxy.`
      );
    }
  }

  async safeFetchJson(input, init) {
    try {
      const res = await fetch(input, init);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(
          `HTTP ${res.status} ${res.statusText}${text ? ` - ${text.slice(0, 200)}` : ''}`
        );
      }
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) return {};
      return await res.json();
    } catch (err) {
      if (err?.name === 'TypeError' && String(err.message).includes('Failed to fetch')) {
        throw new Error(
          'Failed to fetch (network/CORS/mixed-content). Check server CORS, URL, and HTTPS vs HTTP.'
        );
      }
      throw err;
    }
  }

  // --------------- Validation helpers ---------------
  getEffectivePrefix = () => {
    return this.state.addKind === 'device'
      ? 'app:'
      : (this.state.newUserPrefix || 'app:');
  };

  validateIntercomUserName = (rawValue, prefix, addKind) => {
    const value = (rawValue || '').trim();
    const effectivePrefix = addKind === 'device' ? 'app:' : (prefix || 'app:');

    if (!value) {
      return this.transText('user_name_required', 'User name is required');
    }

    if (effectivePrefix === 'app:') {
      if (/\s/.test(value)) {
        return ETVConstants.trans('user_name_no_spaces') || 'User name must not contain spaces';
      }
      return '';
    }

    if (effectivePrefix === 'tel:') {
      // Twilio expects E.164 format: +, country code, and up to 15 total digits.
      if (!/^\+[1-9]\d{6,14}$/.test(value)) {
        return (
          ETVConstants.trans('invalid_phone_number') ||
          'Phone number must be Twilio/E.164 format, e.g. +6591234567'
        );
      }
      return '';
    }

    if (effectivePrefix === 'sip:') {
      if (/\s/.test(value)) {
        return ETVConstants.trans('invalid_sip_address') || 'SIP address must not contain spaces';
      }

      // user@server / user@server.com / user@192.168.1.10 / user@server.com:5060
      const sipRegex =
        /^[a-zA-Z0-9_.!~*'()%+\-]+@((\d{1,3}\.){3}\d{1,3}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+)(?::\d{1,5})?$/;

      if (!sipRegex.test(value)) {
        return (
          ETVConstants.trans('invalid_sip_address') ||
          'SIP address must be valid, e.g. user@server.com'
        );
      }
      return '';
    }

    return '';
  };

  validateIntercomPassword = (rawValue) => {
    const value = (rawValue || '').trim();
    if (!value) {
      return this.transText('password_required', 'Password is required');
    }
    return '';
  };

  normalizeSipDomain = (rawValue) => {
    return (rawValue || '')
      .trim()
      .toLowerCase()
      .replace(/\.(?=:\d{1,5}$|$)/, '');
  };

  validateSipBackupDomain = (primaryDomain, backupDomain) => {
    const backup = (backupDomain || '').trim();
    if (!backup) return '';
    if (/\s/.test(backup)) {
      return ETVConstants.trans('invalid_sip_backup_domain') || 'SIP backup domain must not contain spaces';
    }
    if (this.normalizeSipDomain(primaryDomain) === this.normalizeSipDomain(backup)) {
      return ETVConstants.trans('sip_backup_domain_must_differ') || 'SIP backup domain must be different from the main SIP domain';
    }
    return '';
  };

  // --------------- Device list for dropdown ---------------
  loadAllDevices() {
    this.setState({ isLoading: true });
    const accessToken = localStorage.getItem('etv.access_token') || '';
    const reqUserID = localStorage.getItem('etv.user_id') || '';
    const url =
      ETVConstants.getServerBase() + '/device_mgmt?action=list&owner_id=' + this.props.user_id;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          let sorted = Array.isArray(result.list) ? result.list : [];
          sorted.sort(ETVConstants.compareDevice);
          this.setState({ all_devices: sorted, isLoading: false, isLoaded: true });
        },
        () => {
          this.setState({ all_devices: [], isLoading: false, isLoaded: true });
        }
      );
  }

  // --------------- Intercom users ---------------
  filterUsers(list) {
    const res = [];
    for (const u of list || []) {
      const devId = typeof u.device_id === 'string' ? u.device_id.trim() : '';
      if (!devId) {
        res.push({
          ...u,
          intercom_user_id: u.intercom_user_id != null ? String(u.intercom_user_id) : '',
        });
      }
    }
    return res;
  }

  filterDevices(list) {
    const res = [];
    for (const u of list || []) {
      const devId = typeof u.device_id === 'string' ? u.device_id.trim() : '';
      if (devId) {
        res.push(u);
      }
    }
    return res;
  }

  loadIntercomUsers = async () => {
    this.setState({ isLoading: true, error: null });
    try {
      const { base, reqUserID, accessToken } = this.getEnv();
      this.failIfMixedContent(base);

      const listUrl =
        `${base}/intercom_admin?action=list_users` +
        `&parent_id=${encodeURIComponent(reqUserID)}` +
        `&access_token=${encodeURIComponent(accessToken)}` +
        `&user_id=${encodeURIComponent(reqUserID)}`;

      const result = await this.safeFetchJson(listUrl, { method: 'GET' });
      const all = Array.isArray(result?.list) ? result.list : [];

      const users = this.filterUsers(all);
      const devices = this.filterDevices(all);

      this.setState({
        users,
        devices,
        isLoaded: true,
        isLoading: false,
      });
      return all;
    } catch (error) {
      this.setState({
        users: [],
        devices: [],
        isLoaded: true,
        isLoading: false,
        error,
      });
      return [];
    }
  };

  // --------------- UI helpers ---------------
  showWindow = (id, show) => {
    this.setState({ window: show ? id : '' });
  };

  showInfoMessagePrivate(infoType, title, msg, isSpinning) {
    this.setState({
      window: 'message_div',
      info_type: infoType,
      info_title: title,
      info_message: msg,
      spinning: isSpinning,
    });
  }

  closeInfoMessage() {
    this.setState({
      window: '',
      info_type: '',
      info_title: '',
      info_message: undefined,
      spinning: false,
      waiting_for_reply: false,
      aborted: true,
    });
  }

  showErrorMessage(title, msg) {
    this.showInfoMessagePrivate('error', title, msg, false);
  }

  showSuccessMessage(title, msg) {
    this.showInfoMessagePrivate('success', title, msg, false);
  }

  showInfoMessage(title, msg) {
    this.showInfoMessagePrivate('info', title, msg, false);
  }

  showInfoMessageSpinning(title, msg) {
    this.showInfoMessagePrivate('info', title, msg, true);
  }

  openAddUserDialog = (userPrefix = 'app:', provisionCloudSip = false) => {
    this.setState({
      window: 'add_intercom_user_div',
      addKind: 'user',
      addProvisionCloudSip: provisionCloudSip,
      newDeviceSipSource: 'cloud',
      newUserName: '',
      newFriendlyName:'',
      newDeviceId: '',
      newPassword: '',
      newUserPrefix: userPrefix,
      newSipExtension: '',
      newSipDomain: '',
      newSipBackupDomain: '',
      newSipTransport: 'udp',
      newSipBroadcastIp: '',
      newSipTestExtension: '',
      newEn8128AlarmWorkflowEnabled: false,
      newAlarmConfiguration: null,
      showNewSipPassword: false,
      addSaveAttempted: false,
      addSaving: false,
    });
  };

  openAddDeviceDialog = () => {
    this.setState({
      newCallRouting: directRouting(),
      window: 'add_intercom_user_div',
      addKind: 'device',
      addProvisionCloudSip: false,
      newDeviceSipSource: 'cloud',
      newUserName: '',
      newFriendlyName: '',
      newDeviceId: '',
      newPassword: '',
      newUserPrefix: 'app:',
      newSipExtension: '',
      newSipDomain: '',
      newSipBackupDomain: '',
      newSipTransport: 'udp',
      newSipBroadcastIp: '',
      newSipTestExtension: '',
      newEn8128AlarmWorkflowEnabled: false,
      newAlarmConfiguration: null,
      showNewSipPassword: false,
      addSaveAttempted: false,
      addSaving: false,
    });
  };

    openDTMFDialog = () => {
    this.setState({
      window: 'dtmf_dialog_div',
    });
  };

  handleSelectDeviceForNewUser = (event) => {
    const deviceId = event.target.value || '';
    const dev = this.state.all_devices.find((d) => String(d.id) === String(deviceId));
    const deviceLabel = dev?.device_name || dev?.id || '';

    this.setState((prev) => ({
      newDeviceId: deviceId,
      newUserPrefix: 'app:',
      newUserName: prev.newUserName ? prev.newUserName : deviceLabel,
    }));
  };
  parseIntercomAddress = (userName, sipExtension, sipDomain) => {
    const raw = userName || '';
    const prefixes = ['sip:', 'tel:', 'app:'];
    const prefix = prefixes.find((p) => raw.toLowerCase().startsWith(p)) || 'app:';
    const value = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
    if (prefix === 'sip:') {
      const at = value.indexOf('@');
      return {
        prefix,
        value,
        sipExtension: sipExtension || (at >= 0 ? value.slice(0, at) : value),
        sipDomain: sipDomain || (at >= 0 ? value.slice(at + 1) : ''),
      };
    }
    return { prefix, value, sipExtension: sipExtension || '', sipDomain: sipDomain || '' };
  };

  openEditIntercomUserDialog = (user) => {
    if (!user) return;
    const parsed = this.parseIntercomAddress(user.user_name || '', user.sip_extension || '', user.sip_domain || '');
    this.setState({
      window: 'edit_intercom_user_div',
      editUserId: user.intercom_user_id,
      editUserCloudManaged: this.isCloudSipAccount(user),
      editUserPrefix: parsed.prefix,
      editUserName: parsed.prefix === 'sip:' ? '' : parsed.value,
      editFriendlyName: user.friendly_name || '',
      editUserPhone: user.user_phone || '',
      editUserPassword: '',
      editUserSipExtension: parsed.sipExtension,
      editUserSipDomain: parsed.sipDomain,
      editUserSipPassword: user.sip_password || '',
      editUserSipTransport: (user.sip_transport || 'udp').toLowerCase() === 'tcp' ? 'tcp' : 'udp',
      showEditUserSipPassword: false,
      editUserSaving: false,
    });
  };

  saveIntercomUserEdit = () => {
    const prefix = this.state.editUserPrefix || 'app:';
    const isSip = prefix === 'sip:';
    const isTel = prefix === 'tel:';
    const sipExtension = (this.state.editUserSipExtension || '').trim();
    const sipDomain = (this.state.editUserSipDomain || '').trim();
    const sipPassword = (this.state.editUserSipPassword || '').trim();
    const rawName = (this.state.editUserName || '').trim();
    const userNameValue = isSip ? `${sipExtension}@${sipDomain}` : rawName;
    const userNameError = this.validateIntercomUserName(userNameValue, prefix, 'user');
    const sipError = isSip && (!sipExtension || !sipDomain || !sipPassword)
      ? (ETVConstants.trans('sip_settings_required') || 'SIP extension, domain, and password are required')
      : (isSip && (/\s/.test(sipExtension) || /\s/.test(sipDomain))
        ? (ETVConstants.trans('invalid_sip_settings') || 'SIP settings must not contain spaces')
        : '');

    if (!this.state.editUserId) return;
    if (sipError || userNameError) {
      this.showErrorMessage(ETVConstants.trans('error') || 'Error', sipError || userNameError);
      return;
    }

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = ETVConstants.getServerBase() + '/intercom_admin';
    const body = new URLSearchParams();
    body.set('action', 'update_user');
    body.set('intercom_user_id', String(this.state.editUserId));
    body.set('user_name', `${prefix}${userNameValue}`);
    body.set('friendly_name', (this.state.editFriendlyName || '').trim());
    body.set('user_phone', isTel ? rawName : (this.state.editUserPhone || '').trim());
    body.set('sip_extension', isSip ? sipExtension : '');
    body.set('sip_domain', isSip ? sipDomain : '');
    body.set('sip_password', isSip ? sipPassword : '');
    body.set('sip_transport', isSip && this.state.editUserSipTransport === 'tcp' ? 'tcp' : 'udp');
    body.set('sip_test_extension', '');
    if (isSip && (this.state.editUserPassword || '').trim()) {
      body.set('password', (this.state.editUserPassword || '').trim());
    }

    this.setState({ editUserSaving: true });
    this.showInfoMessageSpinning(ETVConstants.trans('save') || 'Save', ETVConstants.trans('saving') || 'Saving');

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        user_id: reqUserID,
        access_token: accessToken,
      },
      body: body.toString(),
    })
      .then((res) => res.json())
      .then(
        (result) => {
          if (result?.result || result?.success === true) {
            this.setState({
              editUserSaving: false,
              window: '',
              editUserId: null,
              editUserCloudManaged: false,
              editUserPrefix: 'app:',
              editUserName: '',
              editFriendlyName: '',
              editUserPhone: '',
              editUserPassword: '',
              editUserSipExtension: '',
              editUserSipDomain: '',
              editUserSipPassword: '',
              editUserSipTransport: 'udp',
              showEditUserSipPassword: false,
              isLoaded: false,
            });
            return;
          }
          this.setState({ editUserSaving: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'Error', result?.reason || result?.error || 'saving_error');
        },
        (err) => {
          this.setState({ editUserSaving: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'Error', String(err?.message || 'saving_error'));
        }
      );
  };
  openEditDeviceDialog = (device) => {
    if (!device) return;
    this.setState({
      window: 'edit_intercom_device_div',
      editDeviceUserId: device.intercom_user_id,
      originalCallRouting: { ...directRouting(), ...device.call_routing },
      originalDeviceCallerName: (device.friendly_name || '').trim(),
      editAlarmConfiguration: device.alarm_configuration?.version ? device.alarm_configuration : null,
      editAlarmConfigurationStatus: device.alarm_configuration_status || null,
      editCallRouting: { ...directRouting(), ...device.call_routing },
      editDeviceName: device.user_name || '',
      editDeviceCallerName: device.friendly_name || '',
      editDeviceCloudManaged: this.isCloudSipAccount(device),
      editSipExtension: device.sip_extension || '',
      editSipDomain: device.sip_domain || '',
      editSipBackupDomain: device.sip_backup_domain || '',
      editSipPassword: device.sip_password || '',
      editSipTransport: (device.sip_transport || 'udp').toLowerCase() === 'tcp' ? 'tcp' : 'udp',
      editSipBroadcastIp: device.sip_broadcast_ip || '',
      editSipTestExtension: device.sip_test_extension || '',
      editEn8128AlarmWorkflowEnabled:
        device.en81_28_alarm_workflow_enabled === true ||
        device.en81_28_alarm_workflow_enabled === 1 ||
        device.en81_28_alarm_workflow_enabled === '1' ||
        device.en81_28_alarm_workflow_enabled === 'true',
      showEditSipPassword: false,
      editSipSaving: false,
    });
  };

  saveAlarmConfiguration = async (screenID, configuration, enabled) => {
    if (!configuration) return; // Preserve legacy devices until the dialog is used.
    const { base, reqUserID, accessToken } = this.getEnv();
    const body = new URLSearchParams({ action: 'update_alarm_configuration', intercom_user_id: String(screenID), alarm_configuration: JSON.stringify(configuration), en81_28_alarm_workflow_enabled: String(enabled) });
    const result = await this.safeFetchJson(`${base}/intercom_admin`, {
      method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8', user_id: reqUserID, access_token: accessToken }, body: body.toString(),
    });
    if (!result?.result) throw new Error(result?.reason || 'Could not save alarm configuration.');
    return result;
  };

  saveCallRouting = async (screenID, value) => {
    const { base, reqUserID, accessToken } = this.getEnv();
    const body = new URLSearchParams({ action: 'update_call_routing', intercom_user_id: String(screenID) });
    const routing = value || directRouting();
    ['call_mode', 'motor_room_user_id', 'motor_room_address', 'pbx_destination'].forEach(key => body.set(key, routing[key] || ''));
    const result = await this.safeFetchJson(`${base}/intercom_admin`, {
      method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8', user_id: reqUserID, access_token: accessToken }, body: body.toString(),
    });
    if (!result?.result) throw new Error(result?.reason || 'Could not save emergency call routing.');
    return result;
  };

  saveDeviceSipSettings = () => {
    const callError = routingError(this.state.editCallRouting, this.state.editDeviceCloudManaged);
    if (callError) { this.showErrorMessage('Call routing', callError); return; }
    const sipExtension = (this.state.editSipExtension || '').trim();
    const sipDomain = (this.state.editSipDomain || '').trim();
    const sipBackupDomain = (this.state.editSipBackupDomain || '').trim();
    const sipPassword = (this.state.editSipPassword || '').trim();
    const sipTransport = (this.state.editSipTransport || 'udp').trim().toLowerCase() === 'tcp' ? 'tcp' : 'udp';
    const sipBroadcastIp = (this.state.editSipBroadcastIp || '').trim();
    const sipTestExtension = (this.state.editSipTestExtension || '').trim();

    if (!this.state.editDeviceUserId) return;
    if (!sipExtension || !sipDomain || !sipPassword) {
      this.showErrorMessage(
        ETVConstants.trans('error') || 'Error',
        ETVConstants.trans('sip_settings_required') || 'SIP extension, domain, and password are required'
      );
      return;
    }
    const sipBackupDomainError = this.validateSipBackupDomain(sipDomain, sipBackupDomain);
    if (sipBackupDomainError) {
      this.showErrorMessage(ETVConstants.trans('error') || 'Error', sipBackupDomainError);
      return;
    }
    if (
      /\s/.test(sipExtension) ||
      /\s/.test(sipDomain) ||
      (sipBroadcastIp && /\s/.test(sipBroadcastIp)) ||
      (sipTestExtension && /\s/.test(sipTestExtension))
    ) {
      this.showErrorMessage(
        ETVConstants.trans('error') || 'Error',
        ETVConstants.trans('invalid_sip_settings') || 'SIP settings must not contain spaces'
      );
      return;
    }

    this.setState({ editSipSaving: true });
    this.showInfoMessageSpinning(
      ETVConstants.trans('save') || 'Save',
      ETVConstants.trans('saving') || 'Saving'
    );

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = ETVConstants.getServerBase() + '/intercom_admin';
    const body = new URLSearchParams();
    body.set('action', 'update_sip_settings');
    const callerName = (this.state.editDeviceCallerName || '').trim();
    if (callerName !== this.state.originalDeviceCallerName) body.set('friendly_name', callerName);
    body.set('intercom_user_id', String(this.state.editDeviceUserId));
    body.set('sip_extension', sipExtension);
    body.set('sip_domain', sipDomain);
    body.set('sip_backup_domain', sipBackupDomain);
    body.set('sip_password', sipPassword);
    body.set('sip_transport', sipTransport);
    body.set('sip_broadcast_ip', sipBroadcastIp);
    body.set('sip_test_extension', sipTestExtension);
    body.set('sip_test_number', sipTestExtension);
    body.set(
      'en81_28_alarm_workflow_enabled',
      this.state.editEn8128AlarmWorkflowEnabled ? 'true' : 'false'
    );

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        user_id: reqUserID,
        access_token: accessToken,
      },
      body: body.toString(),
    })
      .then((res) => res.json())
      .then(async result => {
        if (result?.result || result?.success === true) {
          // Appearance changes must not invalidate an already-provisioned ring group.
          if (!sameRoutingSettings(this.state.originalCallRouting, this.state.editCallRouting)) {
            await this.saveCallRouting(this.state.editDeviceUserId, this.state.editCallRouting);
            this.setState({ originalCallRouting: { ...this.state.editCallRouting } });
          }
          this.setState({ originalDeviceCallerName: callerName });
          await this.saveAlarmConfiguration(this.state.editDeviceUserId, this.state.editAlarmConfiguration, this.state.editEn8128AlarmWorkflowEnabled);
        }
        return result;
      })
      .then(
        (result) => {
          if (result?.result || result?.success === true) {
            this.setState({
              editSipSaving: false,
              window: '',
              editDeviceUserId: null,
              editDeviceName: '',
              editDeviceCallerName: '',
              editDeviceCloudManaged: false,
              editSipExtension: '',
              editSipDomain: '',
              editSipBackupDomain: '',
              editSipPassword: '',
              editSipTransport: 'udp',
              editSipBroadcastIp: '',
              editSipTestExtension: '',
              editEn8128AlarmWorkflowEnabled: false,
              showEditSipPassword: false,
              isLoaded: false,
            });
            return;
          }
          this.setState({ editSipSaving: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'Error', result?.reason || result?.error || 'saving_error');
        },
        (err) => {
          this.setState({ editSipSaving: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'Error', String(err?.message || 'saving_error'));
        }
      );
  };

  // --------------- Create / Delete intercom user ---------------
  async saveIntercomUser() {
    if (this.state.addProvisionCloudSip) {
      await this.createCloudSipNumber();
      return;
    }
    if (this.state.addSaving) return;
    this.setState({ addSaveAttempted: true });
    const {
      newUserName,
      newFriendlyName,
      newDeviceId,
      newPassword,
      addKind,
      newDeviceSipSource,
      newUserPrefix,
      newSipExtension,
      newSipDomain,
      newSipBackupDomain,
      newSipTransport,
      newSipBroadcastIp,
      newSipTestExtension,
      newEn8128AlarmWorkflowEnabled,
    } = this.state;

    const rawName = (newUserName || '').trim();
    const sipExtension = (newSipExtension || '').trim();
    const sipDomain = (newSipDomain || '').trim();
    const sipBackupDomain = (newSipBackupDomain || '').trim();
    const sipTransport = (newSipTransport || 'udp').trim().toLowerCase();
    const sipBroadcastIp = (newSipBroadcastIp || '').trim();
    const sipTestExtension = (newSipTestExtension || '').trim();
    const pwd = (newPassword || '').trim();
    const prefix = addKind === 'device' ? 'app:' : (newUserPrefix || 'app:');
    const deviceUsesCloudSip = addKind === 'device' && newDeviceSipSource === 'cloud';
    const isSipEntry = addKind === 'user' && prefix === 'sip:';
    const isTelEntry = addKind === 'user' && prefix === 'tel:';
    const callError = addKind === 'device' ? routingError(this.state.newCallRouting, deviceUsesCloudSip) : '';
    if (callError) { this.showErrorMessage('Call routing', callError); return; }
    const needsSipFields = (addKind === 'device' && !deviceUsesCloudSip) || isSipEntry;
    const needsPassword = (addKind === 'device' && !deviceUsesCloudSip) || (addKind === 'user' && !isTelEntry);
    const userNameValue = isSipEntry ? `${sipExtension}@${sipDomain}` : rawName;
    const userNameError = this.validateIntercomUserName(userNameValue, prefix, addKind);
    const passwordError = needsPassword ? this.validateIntercomPassword(pwd) : '';
    const sipExtensionError = needsSipFields && !sipExtension
      ? (ETVConstants.trans('sip_extension_required') || 'SIP extension is required')
      : (needsSipFields && /\s/.test(sipExtension)
        ? (ETVConstants.trans('invalid_sip_extension') || 'SIP extension must not contain spaces')
        : '');
    const sipDomainError = needsSipFields && !sipDomain
      ? (ETVConstants.trans('sip_domain_required') || 'SIP domain is required')
      : (needsSipFields && /\s/.test(sipDomain)
        ? (ETVConstants.trans('invalid_sip_domain') || 'SIP domain must not contain spaces')
        : '');
    const sipBackupDomainError = needsSipFields
      ? this.validateSipBackupDomain(sipDomain, sipBackupDomain)
      : '';
    const sipTestExtensionError = sipTestExtension && /\s/.test(sipTestExtension)
      ? (ETVConstants.trans('invalid_sip_test_extension') || 'SIP test extension must not contain spaces')
      : '';
    const sipBroadcastIpError = sipBroadcastIp && /\s/.test(sipBroadcastIp)
      ? (ETVConstants.trans('invalid_sip_broadcast_ip') || 'SIP broadcast IP must not contain spaces')
      : '';

    const deviceRequiredError = addKind === 'device' && !(newDeviceId || '').trim()
      ? this.transText('device_required', 'Device is required')
      : '';

    if (
      sipExtensionError ||
      sipDomainError ||
      sipBackupDomainError ||
      sipBroadcastIpError ||
      sipTestExtensionError ||
      userNameError ||
      passwordError ||
      deviceRequiredError
    ) {
      return;
    }

    const fullUserName = `${prefix}${userNameValue}`;
    const generatedDevicePassword = window.crypto?.randomUUID?.()
      || `screen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const submittedPassword = isTelEntry
      ? 'tel-user'
      : (deviceUsesCloudSip ? generatedDevicePassword : pwd);

    this.setState({ addSaving: true });

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = ETVConstants.getServerBase() + '/intercom_admin';

    const body = new URLSearchParams();
    body.set('action', 'add_user');
    body.set('user_name', fullUserName);
    if (addKind === 'user') {
      body.set('friendly_name', (newFriendlyName || '').trim());
    }
    body.set('device_id', addKind === 'device' ? (newDeviceId || '') : '');
    body.set('parent_id', reqUserID);
    body.set('password', submittedPassword);
    if (needsSipFields) {
      body.set('sip_username', sipExtension);
      body.set('sip_extension', sipExtension);
      body.set('sip_domain', sipDomain);
      body.set('sip_backup_domain', sipBackupDomain);
      body.set('sip_password', submittedPassword);
      body.set('sip_transport', sipTransport === 'tcp' ? 'tcp' : 'udp');
      body.set('sip_broadcast_ip', sipBroadcastIp);
      body.set('sip_test_number', sipTestExtension);
      body.set('sip_test_extension', sipTestExtension);
      if (sipTestExtension) {
        body.set('testcall_address', 'sip:' + sipTestExtension + '@' + sipDomain);
      }
    }
    if (addKind === 'device') {
      body.set(
        'en81_28_alarm_workflow_enabled',
        newEn8128AlarmWorkflowEnabled ? 'true' : 'false'
      );
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const result = await this.safeFetchJson(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          user_id: reqUserID,
          access_token: accessToken,
        },
        body: body.toString(),
        signal: controller.signal,
      });

      if (result?.result) {
        if (addKind === 'device') {
          const allIntercomUsers = await this.loadIntercomUsers();
          const createdDevice = allIntercomUsers.find(
            (entry) => String(entry?.device_id || '') === String(newDeviceId || '')
          );
          if (!createdDevice?.intercom_user_id) {
            this.setState({ addSaving: false });
            throw new Error(this.transText(
              'linked_screen_not_found',
              'The screen was linked, but its Cloud PBX account could not be allocated automatically.'
            ));
          }
          if (deviceUsesCloudSip && !await this.allocateCloudSipAccount(createdDevice, false)) {
            this.openEditDeviceDialog(createdDevice);
            throw new Error('The screen was linked, but Cloud SIP allocation failed. Retry allocation before enabling call routing.');
          }
          try {
            await this.saveCallRouting(createdDevice.intercom_user_id, this.state.newCallRouting);
            await this.saveAlarmConfiguration(createdDevice.intercom_user_id, this.state.newAlarmConfiguration, this.state.newEn8128AlarmWorkflowEnabled);
          } catch (error) {
            const refreshed = (await this.loadIntercomUsers()).find(u => u.intercom_user_id === createdDevice.intercom_user_id) || createdDevice;
            this.openEditDeviceDialog(refreshed);
            this.setState({ editAlarmConfiguration: this.state.newAlarmConfiguration, editEn8128AlarmWorkflowEnabled: this.state.newEn8128AlarmWorkflowEnabled });
            throw new Error(`The screen was linked, but saving its routing or alarm configuration failed: ${error.message}. Review and save the device settings again.`);
          }
        }
        this.setState({
          isLoaded: false,
          ...(deviceUsesCloudSip ? {} : { window: '' }),
          newUserName: '',
          newFriendlyName:'',
          newPassword: '',
          newDeviceId: '',
          newUserPrefix: 'app:',
          newSipExtension: '',
          newSipDomain: '',
          newSipBackupDomain: '',
          newSipTransport: 'udp',
          newSipBroadcastIp: '',
          newSipTestExtension: '',
          newEn8128AlarmWorkflowEnabled: false,
      newAlarmConfiguration: null,
          showNewSipPassword: false,
          addSaveAttempted: false,
          addSaving: false,
          addKind: 'user',
          addProvisionCloudSip: false,
          newDeviceSipSource: 'cloud',
        });
        return;
      }

      const msg = result?.reason || result?.error || 'saving_error';
      this.setState({ addSaving: false }, () => {
        if (msg === 'user_exists') {
          this.showErrorMessage(
            ETVConstants.trans('error'),
            ETVConstants.trans('intercom_exists_please_choose_different_name')
          );
        } else if (msg === 'device_exists') {
          this.showErrorMessage(
            ETVConstants.trans('error'),
            ETVConstants.trans('device_already_linked_to_intercom_user_name')
          );
        } else {
          this.showErrorMessage(ETVConstants.trans('error') || 'error', msg);
        }
      });
    } catch (err) {
      const message = err?.name === 'AbortError'
        ? 'The server did not respond within 20 seconds. Please try again.'
        : String(err?.message || 'saving_error');
      this.setState({ addSaving: false }, () => {
        this.showErrorMessage(ETVConstants.trans('error') || 'error', message);
      });
    } finally {
      window.clearTimeout(timeout);
    }
  }

  deleteIntercomUser(intercomUserID) {
    if (!intercomUserID) return;

    this.showInfoMessageSpinning(
      ETVConstants.trans('delete'),
      ETVConstants.trans('deleting')
    );

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    let url = ETVConstants.getServerBase() + '/intercom_admin?action=delete_user';
    url += '&intercom_user_id=' + encodeURIComponent(intercomUserID);

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then(async (res) => {
        const result = await res.json();
        if (!res.ok || !result?.result) {
          throw new Error(result?.reason || result?.error || 'Could not remove the intercom registration.');
        }
        return result;
      })
      .then(() => {
          this.setState({
            isLoaded: false,
            window: '',
          });
      })
      .catch((error) => {
        this.showErrorMessage(this.transText('delete_error', 'Unable to remove intercom registration'), error.message);
      });
  }

  // --------------- Change password ---------------
  openChangePasswordDialog = (user) => {
    this.setState({
      changePwUserId: user.intercom_user_id,
      changePwUserName: user.user_name || '',
      changePwNew: '',
      changePwConfirm: '',
    });
    this.showWindow('change_password_div', true);
  };

  submitChangePassword = () => {
    const pw = (this.state.changePwNew || '').trim();
    const conf = (this.state.changePwConfirm || '').trim();

    if (!this.state.changePwUserId || !pw || pw !== conf) return;

    this.setState({ changePwSaving: true });
    this.showInfoMessageSpinning(
      ETVConstants.trans('change_password'),
      ETVConstants.trans('change_password')
    );

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = ETVConstants.getServerBase() + '/intercom_admin';

    const body = new URLSearchParams();
    body.set('action', 'change_password');
    body.set('intercom_user_id', String(this.state.changePwUserId));
    body.set('password', pw);

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        user_id: reqUserID,
        access_token: accessToken,
      },
      body: body.toString(),
    })
      .then((res) => res.json())
      .then(
        (result) => {
          if (result?.success === false) {
            this.showErrorMessage('password_change_error', result?.error || '');
            this.setState({ changePwSaving: false });
            return;
          }

          this.showSuccessMessage(
            ETVConstants.trans('success'),
            ETVConstants.trans('password_changed')
          );

          this.setState({
            changePwSaving: false,
            window: '',
            changePwUserId: null,
            changePwUserName: '',
            changePwNew: '',
            changePwConfirm: '',
          });
        },
        () => {
          this.setState({ changePwSaving: false });
          this.showErrorMessage('password_change_error');
        }
      );
  };

  // --------------- Assignment dialog ---------------
  showAssignDialog = (deviceID) => {
    this.setState({
      selected_device_id: deviceID,
      window: 'assign_user_dialog_div',
    });
  };

  printAddress(location) {
    if (location == null || location === '') return '';
    if (typeof location === 'string') return location;
    if (typeof location === 'number' || typeof location === 'boolean') return String(location);
    if (typeof location === 'object') {
      return location.name || location.address || location.label || location.value || '';
    }
    return '';
  }

  transText(key, fallback = '') {
    const value = ETVConstants.trans(key);
    if (typeof value === 'string' || typeof value === 'number') {
      const text = String(value);
      return text === `[${key}]` ? fallback : text;
    }
    return fallback;
  }

  openGrandstreamImport = () => {
    this.setState({
      window:'grandstream_import_div',
      grandstreamIp:'',
      grandstreamContacts:[],
      grandstreamStep:'address',
      grandstreamLoading:false,
      grandstreamSaving:false,
      grandstreamError:'',
    });
  };

  openAddCloudPbxDialog = () => this.openAddUserDialog('sip:', true);

  openAddAppUserDialog = () => this.openAddUserDialog('app:', false);

  openAddOnPremiseSipDialog = () => this.openAddUserDialog('sip:', false);

  isCloudSipAccount = (user) => {
    if (typeof user?.call_routing?.cloud_managed === 'boolean') return user.call_routing.cloud_managed;
    const extension = String(user?.sip_extension || '').trim();
    const domain = String(user?.sip_domain || '').trim().toLowerCase();
    return /^[68]\d{5}$/.test(extension) && domain.endsWith('.elevator.tv');
  };

  createCloudSipNumber = async () => {
    if (this.state.addSaving) return;
    const friendlyName = (this.state.newFriendlyName || '').trim();
    this.setState({ addSaveAttempted: true });
    if (!friendlyName) return;

    this.setState({ addSaving: true });
    try {
      const { base, reqUserID, accessToken } = this.getEnv();
      this.failIfMixedContent(base);
      const body = new URLSearchParams();
      body.set('action', 'create_external');
      body.set('friendly_name', friendlyName);
      body.set('region', 'SG');
      const result = await this.safeFetchJson(`${base}/cloud_sip_admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          user_id: reqUserID,
          access_token: accessToken,
        },
        body: body.toString(),
      });
      if (!result?.result || !result?.account?.sip_password) {
        throw new Error(result?.reason || 'Cloud SIP allocation failed');
      }
      await this.loadIntercomUsers();
      this.setState({
        window: 'cloud_sip_credentials_div',
        cloudSipCredentials: result.account,
        addSaving: false,
        addSaveAttempted: false,
        addProvisionCloudSip: false,
        newFriendlyName: '',
      });
    } catch (error) {
      this.setState({ addSaving: false });
      this.showErrorMessage(
        this.transText('cloud_pbx_error', 'Cloud PBX error'),
        String(error?.message || error)
      );
    }
  };

  allocateCloudSipAccount = async (user, showCredentials = true) => {
    const intercomUserId = user?.intercom_user_id;
    if (!intercomUserId || this.state.cloudSipBusyUserId) return;
    this.setState({ cloudSipBusyUserId: intercomUserId });
    try {
      const { base, reqUserID, accessToken } = this.getEnv();
      this.failIfMixedContent(base);
      const body = new URLSearchParams();
      body.set('action', 'allocate');
      body.set('intercom_user_id', intercomUserId);
      body.set('region', 'SG');
      const result = await this.safeFetchJson(`${base}/cloud_sip_admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          user_id: reqUserID,
          access_token: accessToken,
        },
        body: body.toString(),
      });
      if (!result?.result || !result?.account) {
        throw new Error(result?.reason || 'Cloud SIP allocation failed');
      }
      await this.loadIntercomUsers();
      if (showCredentials && result.account.sip_password) {
        this.setState({
          window: 'cloud_sip_credentials_div',
          cloudSipCredentials: result.account,
        });
      } else {
        this.showSuccessMessage(
          this.transText('cloud_pbx_ready', 'Cloud PBX account ready'),
          `${result.account.extension} @ ${result.account.sip_domain}`
        );
      }
      return result.account;
    } catch (error) {
      this.showErrorMessage(
        this.transText('cloud_pbx_error', 'Cloud PBX error'),
        String(error?.message || error)
      );
    } finally {
      this.setState({ cloudSipBusyUserId: null });
    }
    return null;
  };

  openDisableCloudSipDialog = (user) => {
    if (!user?.intercom_user_id || this.state.cloudSipBusyUserId) return;
    this.setState({
      cloudSipPendingRemoval: user,
      window: 'remove_cloud_pbx_div',
    });
  };

  disableCloudSipAccount = async () => {
    const user = this.state.cloudSipPendingRemoval;
    const intercomUserId = user?.intercom_user_id;
    if (!intercomUserId || this.state.cloudSipBusyUserId) return;
    const extension = String(user?.sip_extension || '').trim();
    this.setState({ cloudSipBusyUserId: intercomUserId });
    try {
      const { base, reqUserID, accessToken } = this.getEnv();
      this.failIfMixedContent(base);
      const body = new URLSearchParams();
      body.set('action', 'disable');
      body.set('intercom_user_id', intercomUserId);
      const result = await this.safeFetchJson(`${base}/cloud_sip_admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          user_id: reqUserID,
          access_token: accessToken,
        },
        body: body.toString(),
      });
      if (!result?.result) {
        throw new Error(result?.reason || 'Could not remove cloud PBX account');
      }
      await this.loadIntercomUsers();
      this.setState({ cloudSipPendingRemoval: null });
      this.showSuccessMessage(
        this.transText('cloud_pbx_removed', 'Cloud PBX account removed'),
        extension
      );
    } catch (error) {
      this.setState({ cloudSipPendingRemoval: null });
      this.showErrorMessage(
        this.transText('cloud_pbx_error', 'Cloud PBX error'),
        String(error?.message || error)
      );
    } finally {
      this.setState({ cloudSipBusyUserId: null });
    }
  };

  previewGrandstreamContacts = async () => {
    const ipAddress = (this.state.grandstreamIp || '').trim();
    if(!ipAddress || this.state.grandstreamLoading) return;
    const { base, reqUserID, accessToken } = this.getEnv();
    const body = new URLSearchParams();
    body.set('action', 'grandstream_preview');
    body.set('ip_address', ipAddress);
    this.setState({grandstreamLoading:true,grandstreamError:''});
    try {
      const response = await fetch(`${base}/intercom_admin`, {
        method:'POST',
        headers:{
          'Content-Type':'application/x-www-form-urlencoded;charset=UTF-8',
          user_id:reqUserID,
          access_token:accessToken,
        },
        body:body.toString(),
      });
      const result = await response.json();
      if(!response.ok || result?.result === false) {
        throw new Error(result?.reason || result?.error || `http_${response.status}`);
      }
      const contacts = (Array.isArray(result?.list) ? result.list : []).map((contact,index)=>({
        ...contact,
        importKey:`${contact.phone || ''}-${index}`,
        selected:true,
      }));
      this.setState({
        grandstreamContacts:contacts,
        grandstreamStep:'preview',
        grandstreamLoading:false,
        grandstreamError:contacts.length ? '' : this.transText('grandstream_no_contacts', 'No contacts were found in the Grandstream phonebook.'),
      });
    }catch(error) {
      this.setState({grandstreamLoading:false,grandstreamError:String(error?.message || error)});
    }
  };

  toggleGrandstreamContact = (importKey) => {
    this.setState((previous)=>({
      grandstreamContacts:previous.grandstreamContacts.map((contact)=>
        contact.importKey===importKey ? {...contact,selected:!contact.selected} : contact
      ),
    }));
  };

  saveGrandstreamContacts = async () => {
    const selected = this.state.grandstreamContacts.filter((contact)=>contact.selected);
    if(!selected.length || this.state.grandstreamSaving) return;
    const { base, reqUserID, accessToken } = this.getEnv();
    this.setState({grandstreamSaving:true,grandstreamError:''});
    try {
      let imported = 0;
      let skipped = 0;
      for(const contact of selected) {
        const phone = String(contact.phone || '').trim();
        if(!phone) {
          skipped++;
          continue;
        }
        const body = new URLSearchParams();
        body.set('action', 'add_user');
        body.set('user_name', `tel:${phone}`);
        body.set('friendly_name', String(contact.name || phone).trim());
        body.set('user_phone', phone);
        body.set('password', window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
        body.set('parent_id', reqUserID);
        body.set('device_id', '');
        const response = await fetch(`${base}/intercom_admin`, {
          method:'POST',
          headers:{
            'Content-Type':'application/x-www-form-urlencoded;charset=UTF-8',
            user_id:reqUserID,
            access_token:accessToken,
          },
          body:body.toString(),
        });
        const result = await response.json();
        if(response.ok && result?.result !== false) {
          imported++;
        }else if(result?.reason === 'user_exists') {
          skipped++;
        }else{
          throw new Error(result?.reason || result?.error || `http_${response.status}`);
        }
      }
      this.setState({
        window:'',
        grandstreamSaving:false,
        grandstreamContacts:[],
        grandstreamStep:'address',
      });
      await this.loadIntercomUsers();
      this.showSuccessMessage(
        this.transText('grandstream_import_complete', 'Grandstream import complete'),
        `${imported} ${this.transText('contacts_imported', 'contacts imported')}, ${skipped} ${this.transText('contacts_skipped', 'skipped')}.`
      );
    }catch(error) {
      this.setState({grandstreamSaving:false,grandstreamError:String(error?.message || error)});
    }
  };

  // --------------- Render ---------------
  render() {
    const {
      users,
      devices,
      all_devices,
      isLoading,
      error,
      window,
      newUserName,
      newDeviceId,
      selected_device_id,
      addKind,
      addProvisionCloudSip,
      newDeviceSipSource,
      newPassword,
      newUserPrefix,
      newSipExtension,
      newSipDomain,
      newSipBackupDomain,
      newSipTransport,
      newSipBroadcastIp,
      newSipTestExtension,
    } = this.state;

    if (this.props.view === 'alarms' || this.props.view === 'supervision') {
      const isAlarmOverview = this.props.view === 'alarms';
      return (
        <ETVPage width="standard" icon={<PhoneInTalkOutlinedIcon fontSize="large"/>}
          title={isAlarmOverview
            ? this.transText('en81_alarm_overview', 'EN81-28 alarm overview')
            : this.transText('alarm_path_supervision', 'Alarm Path Supervision')}
          loading={isLoading}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{String(error?.message || error)}</Alert>}
          {isAlarmOverview
            ? <IntercomAlarmOverview parentId={localStorage.getItem('etv.user_id') || this.props.user_id} devices={devices} />
            : <IntercomAlarmTestOverview parentId={localStorage.getItem('etv.user_id') || this.props.user_id} devices={devices} />}
        </ETVPage>
      );
    }

    const effectivePrefix = addKind === 'device' ? 'app:' : (newUserPrefix || 'app:');
    const isAddingDevice = addKind === 'device';
    const deviceUsesCloudSip = isAddingDevice && newDeviceSipSource === 'cloud';
    const isCloudPbxAddition = !isAddingDevice && addProvisionCloudSip;
    const isSipUser = !isAddingDevice && !isCloudPbxAddition && effectivePrefix === 'sip:';
    const isTelUser = !isAddingDevice && effectivePrefix === 'tel:';
    const showSipFields = (isAddingDevice && !deviceUsesCloudSip) || isSipUser;
    const showVisiblePassword = !isCloudPbxAddition
      && ((isAddingDevice && !deviceUsesCloudSip) || (!isTelUser && !isAddingDevice));
    const showAddValidation = !!this.state.addSaveAttempted;
    const addDialogTitle = isAddingDevice
      ? this.transText('link_elevator_screen', 'Link elevator screen')
      : isCloudPbxAddition
        ? this.transText('add_cloud_pbx_number', 'Add Cloud PBX Number')
        : isSipUser
          ? this.transText('add_onpremise_sip_number', 'Add On-premise SIP Number')
          : this.transText('add_app_user', 'Add App User');
    const addDialogDesc = isAddingDevice
      ? (ETVConstants.trans('add_intercom_device_desc') || 'Create an intercom device login and link it to an existing screen device.')
      : isCloudPbxAddition
        ? this.transText('add_cloud_pbx_number_desc', 'Create credentials for an external SIP phone or softphone on the managed Cloud PBX.')
        : isSipUser
          ? this.transText('add_onpremise_sip_number_desc', 'Create a SIP number registered with your on-premise PBX.')
          : this.transText('add_app_user_desc', 'Create a user for the Elevator.TV intercom app.');
    const userNameValidationValue = isSipUser
      ? `${(newSipExtension || '').trim()}@${(newSipDomain || '').trim()}`
      : newUserName;
    const userNameError = isCloudPbxAddition
      ? ''
      : this.validateIntercomUserName(userNameValidationValue, effectivePrefix, addKind);
    const passwordError = showVisiblePassword ? this.validateIntercomPassword(newPassword) : '';
    const cloudFriendlyNameError = isCloudPbxAddition && !(this.state.newFriendlyName || '').trim()
      ? this.transText('friendly_name_required', 'Display name is required')
      : '';
    const linkedDeviceIds = new Set((devices || []).map((d) => String(d.device_id || '').trim()).filter(Boolean));
    const availableDevices = (all_devices || []).filter((d) => !linkedDeviceIds.has(String(d.id || '').trim()));
    const sipExtensionError = showSipFields && !(newSipExtension || '').trim()
      ? (ETVConstants.trans('sip_extension_required') || 'SIP extension is required')
      : (showSipFields && /\s/.test((newSipExtension || '').trim())
        ? (ETVConstants.trans('invalid_sip_extension') || 'SIP extension must not contain spaces')
        : '');
    const sipDomainError = showSipFields && !(newSipDomain || '').trim()
      ? (ETVConstants.trans('sip_domain_required') || 'SIP domain is required')
      : (showSipFields && /\s/.test((newSipDomain || '').trim())
        ? (ETVConstants.trans('invalid_sip_domain') || 'SIP domain must not contain spaces')
        : '');
    const sipBackupDomainError = showSipFields
      ? this.validateSipBackupDomain(newSipDomain, newSipBackupDomain)
      : '';
    const sipTestExtensionError = (isAddingDevice || isSipUser) && (newSipTestExtension || '').trim() && /\s/.test((newSipTestExtension || '').trim())
      ? (ETVConstants.trans('invalid_sip_test_extension') || 'SIP test extension must not contain spaces')
      : '';
    const sipBroadcastIpError = showSipFields && (newSipBroadcastIp || '').trim() && /\s/.test((newSipBroadcastIp || '').trim())
      ? (ETVConstants.trans('invalid_sip_broadcast_ip') || 'SIP broadcast IP must not contain spaces')
      : '';
    const deviceRequiredError = isAddingDevice && !(newDeviceId || '').trim()
      ? this.transText('device_required', 'Device is required')
      : '';


    const pw = (this.state.changePwNew || '').trim();
    const conf = (this.state.changePwConfirm || '').trim();
    const canSavePw =
      !this.state.changePwSaving &&
      !!this.state.changePwUserId &&
      !!pw &&
      pw === conf;

    const editPrefix = this.state.editUserPrefix || 'app:';
    const editIsSip = editPrefix === 'sip:';
    const editIsTel = editPrefix === 'tel:';
    const editUserCloudManaged = !!this.state.editUserCloudManaged;
    const editDeviceCloudManaged = !!this.state.editDeviceCloudManaged;
    const editSipExtension = (this.state.editUserSipExtension || '').trim();
    const editSipDomain = (this.state.editUserSipDomain || '').trim();
    const editSipPassword = (this.state.editUserSipPassword || '').trim();
    const editRawName = (this.state.editUserName || '').trim();
    const editUserNameValue = editIsSip ? `${editSipExtension}@${editSipDomain}` : editRawName;
    const editUserNameError = this.validateIntercomUserName(editUserNameValue, editPrefix, 'user');
    const editSipError = editIsSip && (!editSipExtension || !editSipDomain || !editSipPassword)
      ? (ETVConstants.trans('sip_settings_required') || 'SIP extension, domain, and password are required')
      : (editIsSip && (/\s/.test(editSipExtension) || /\s/.test(editSipDomain))
        ? (ETVConstants.trans('invalid_sip_settings') || 'SIP settings must not contain spaces')
        : '');
    const editDeviceSipBackupDomainError = this.validateSipBackupDomain(
      this.state.editSipDomain,
      this.state.editSipBackupDomain
    );
    const canSaveEditUser =
      !this.state.editUserSaving &&
      !!this.state.editUserId &&
      !editUserNameError &&
      !editSipError;

    const getIntercomType = (row) => {
      const name = row?.user_name || '';
      if (name.toLowerCase().startsWith('sip:')) return { label: 'SIP', color: 'info', icon: <Dialpad fontSize="small" /> };
      if (name.toLowerCase().startsWith('tel:')) return { label: 'Tel', color: 'success', icon: <Phone fontSize="small" /> };
      return { label: 'App', color: 'primary', icon: <Apps fontSize="small" /> };
    };

    const stripIntercomPrefix = (name) => String(name || '').replace(/^(sip:|tel:|app:)/i, '');

    const renderEmptyState = (message) => (
      <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="body2">{message}</Typography>
      </Box>
    );

    const renderActionButton = (title, icon, onClick, color = 'primary', disabled = false) => (
      <Tooltip title={title}>
        <span>
          <IconButton color={color} size="small" onClick={onClick} disabled={disabled} sx={{ border: '1px solid', borderColor: 'divider' }}>
          {icon}
          </IconButton>
        </span>
      </Tooltip>
    );

    const renderOnPremiseBadge = () => (
      <Chip
        size="small"
        color="secondary"
        icon={<Router fontSize="small" />}
        label={this.transText('onpremise_pbx', 'On-premise PBX')}
      />
    );

    const renderUserRow = (user) => {
      const type = getIntercomType(user);
      const cloudSip = this.isCloudSipAccount(user);
      const onPremiseSip = type.label === 'SIP' && !cloudSip;
      const sipAddress = stripIntercomPrefix(user.user_name);
      return (
        <Box
          key={user.intercom_user_id || user.user_name}
          sx={(t) => ({
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
            gap: 1.5,
            alignItems: 'center',
            p: 1.5,
            border: `1px solid ${t.palette.divider}`,
            borderRadius: 1,
            bgcolor: 'background.paper',
          })}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Chip size="small" color={type.color} icon={type.icon} label={type.label} />
              {cloudSip ? (
                <>
                  <Chip size="small" color="success" icon={<CloudQueue fontSize="small" />} label="Cloud PBX" />
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`${user.sip_extension}@${user.sip_domain}`}
                  />
                </>
              ) : onPremiseSip ? (
                <>
                  {renderOnPremiseBadge()}
                  <Chip
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label={sipAddress}
                  />
                </>
              ) : (
                <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                  {sipAddress}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {user.friendly_name || this.transText('no_friendly_name', 'No friendly name')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            {renderActionButton(this.transText('edit_intercom_user', 'Edit intercom user'), <Edit fontSize="small" />, () => this.openEditIntercomUserDialog(user))}
            {renderActionButton(this.transText('change_password', 'Change password'), <Password fontSize="small" />, () => this.openChangePasswordDialog(user), 'success')}
            {renderActionButton(this.transText('delete', 'Delete'), <Delete fontSize="small" />, () => this.deleteIntercomUser(user.intercom_user_id), 'error')}
          </Stack>
        </Box>
      );
    };

    const renderDeviceRow = (device) => {
      const cloudSip = this.isCloudSipAccount(device);
      const onPremiseSip = !cloudSip
        && String(device.sip_extension || '').trim()
        && String(device.sip_domain || '').trim();
      return (
      <Box
        key={device.intercom_user_id || device.device_id || device.user_name}
        sx={(t) => ({
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
          gap: 1.5,
          alignItems: 'center',
          p: 1.5,
          border: `1px solid ${t.palette.divider}`,
          borderRadius: 1,
          bgcolor: 'background.paper',
        })}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" color="primary" icon={<Devices fontSize="small" />} label={this.transText('device', 'Device')} />
            {cloudSip && (
              <Chip size="small" color="success" icon={<CloudQueue fontSize="small" />} label="Cloud PBX" />
            )}
            {onPremiseSip && renderOnPremiseBadge()}
            {(cloudSip || onPremiseSip) && (
              <Chip
                size="small"
                color={cloudSip ? 'success' : 'secondary'}
                variant="outlined"
                label={`${device.sip_extension}@${device.sip_domain}`}
              />
            )}
            <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
              {stripIntercomPrefix(device.user_name)}
            </Typography>
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {this.transText('linked_device', 'Linked device') + ': '} {this.printAddress(device.device_name || device.device_id) || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {this.transText('commission_nr', 'Commission number') + ': '} {device.commission_nr || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {this.transText('location', 'Location') + ': '} {this.printAddress(device.location) || '-'}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
          {renderActionButton(this.transText('edit_device_settings', 'Edit device settings'), <Edit fontSize="small" />, () => this.openEditDeviceDialog(device))}
          {renderActionButton(this.transText('show_assignments', 'Show assignments'), <Assignment fontSize="small" />, () => this.showAssignDialog(device.device_id))}
          {renderActionButton(this.transText('change_password', 'Change password'), <Password fontSize="small" />, () => this.openChangePasswordDialog(device), 'success')}
          {renderActionButton(this.transText('unlink_intercom_screen', 'Unlink screen from intercom'), <Delete fontSize="small" />, () => this.deleteIntercomUser(device.intercom_user_id), 'error')}
        </Stack>
      </Box>
      );
    };
    return (
      <>
        <ETVPage width="standard" icon={<PhoneInTalkOutlinedIcon fontSize="large"/>} title={this.transText('configuration', 'Configuration')}
          description="Manage intercom devices, users, assignments, and call settings." loading={isLoading}
          topActions={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
                <Button size="medium" variant="contained" startIcon={<Dialpad />} onClick={this.openDTMFDialog} sx={{ minWidth: 150 }}>
                  {ETVConstants.trans('dtmf_commands')}
                </Button>
                <Button size="medium" variant="contained" startIcon={<CloudDownload />} onClick={this.openGrandstreamImport} sx={{ minWidth: 210 }}>
                  {this.transText('import_from_grandstream', 'Import from Grandstream')}
                </Button>
                <Button size="medium" variant="contained" startIcon={<Devices />} onClick={this.openAddDeviceDialog} sx={{ minWidth: 180 }}>
                  {this.transText('link_elevator_screen', 'Link elevator screen')}
                </Button>
                <Button size="medium" variant="contained" startIcon={<AddCircleOutline />} onClick={this.openAddCloudPbxDialog} sx={{ minWidth: 190 }}>
                  {this.transText('add_cloud_pbx_number', 'Add Cloud PBX Number')}
                </Button>
                <Button size="medium" variant="contained" startIcon={<AddCircleOutline />} onClick={this.openAddAppUserDialog} sx={{ minWidth: 150 }}>
                  {this.transText('add_app_user', 'Add App User')}
                </Button>
                <Button size="medium" variant="contained" startIcon={<AddCircleOutline />} onClick={this.openAddOnPremiseSipDialog} sx={{ minWidth: 220 }}>
                  {this.transText('add_onpremise_sip_number', 'Add On-premise SIP Number')}
                </Button>
              </Box>
          }>
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {(ETVConstants.trans('error') || 'Error') + ': '}
                {String(error?.message || error)}
              </Alert>
            )}

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {this.transText('linked_elevator_screens', 'Linked Elevator Screens')}
                </Typography>

                <Stack spacing={1.25}>
                  {(devices || []).length === 0
                    ? renderEmptyState(ETVConstants.trans('no_intercom_devices') || 'No intercom devices yet')
                    : devices.map(renderDeviceRow)}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {this.transText('users_or_3rd_party_sip_devices', 'Users or 3rd party devices')}
                </Typography>

                <Stack spacing={1.25}>
                  {(users || []).length === 0
                    ? renderEmptyState(ETVConstants.trans('no_intercom_users') || 'No intercom users yet')
                    : users.map(renderUserRow)}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </ETVPage>

        {/* Info dialog */}
        <ETVMessagePopup
          open={this.state.window === 'message_div'}
		  type={this.state.spinning?'progress':this.state.info_type}
		  allowClose={!this.state.spinning}
          onClose={() => this.closeInfoMessage()}
          title={this.state.info_title}
          message={this.state.info_message}
        />

        <ETVPopup
          open={window === 'cloud_sip_credentials_div'}
          onClose={() => this.setState({ window: '', cloudSipCredentials: null })}
          width="small"
          icon={<CloudQueue />}
          title={this.transText('cloud_pbx_credentials', 'Cloud PBX SIP Credentials')}
          description={this.transText('cloud_pbx_credentials_desc', 'Configure these values on the external SIP phone or softphone.')}
          contentSx={{display:'grid',gap:2}}
          bottomActions={<ETVActionButton action="cancel" label={this.transText('close', 'Close')} onClick={() => this.setState({ window: '', cloudSipCredentials: null })}/>}
        >
          <TextField
            label={this.transText('sip_address', 'SIP address')}
            value={`sip:${this.state.cloudSipCredentials?.extension || ''}@${this.state.cloudSipCredentials?.sip_domain || ''}`}
            disabled
            fullWidth
          />
          <TextField
            label={ETVConstants.trans('sip_extension') || 'SIP extension'}
            value={this.state.cloudSipCredentials?.extension || ''}
            disabled
            fullWidth
          />
          <TextField
            label={ETVConstants.trans('sip_domain') || 'SIP domain'}
            value={this.state.cloudSipCredentials?.sip_domain || ''}
            disabled
            fullWidth
          />
          {!!this.state.cloudSipCredentials?.sip_backup_domain && (
            <TextField
              label={ETVConstants.trans('sip_backup_domain') || 'SIP backup domain'}
              value={this.state.cloudSipCredentials.sip_backup_domain}
              disabled
              fullWidth
            />
          )}
          <TextField
            label={ETVConstants.trans('sip_transport') || 'SIP transport'}
            value={(this.state.cloudSipCredentials?.sip_transport || 'udp').toUpperCase()}
            disabled
            fullWidth
          />
          <TextField
            label={ETVConstants.trans('sip_password') || 'SIP password'}
            value={this.state.cloudSipCredentials?.sip_password || ''}
            disabled
            fullWidth
          />
        </ETVPopup>

        <ETVPopup
          open={window === 'remove_cloud_pbx_div'}
          onClose={() => (this.state.cloudSipBusyUserId ? null : this.setState({
            window: '',
            cloudSipPendingRemoval: null,
          }))}
          width="compact"
          icon={<CloudOff />}
          title={this.transText('remove_cloud_pbx', 'Remove from Cloud PBX')}
          description={this.state.cloudSipPendingRemoval?.friendly_name
            || this.state.cloudSipPendingRemoval?.user_name
            || ''}
          loading={!!this.state.cloudSipBusyUserId}
          bottomActions={<>
            <ETVActionButton
              action="cancel"
              disabled={!!this.state.cloudSipBusyUserId}
              onClick={() => this.setState({ window: '', cloudSipPendingRemoval: null })}
            />
            <ETVActionButton
              action="delete"
              label={this.transText('remove', 'Remove')}
              loading={!!this.state.cloudSipBusyUserId}
              onClick={this.disableCloudSipAccount}
            />
          </>}
        >
          <Alert severity="warning">
            {`Cloud PBX extension ${this.state.cloudSipPendingRemoval?.sip_extension || ''} will be disabled. The intercom user itself will remain available.`}
          </Alert>
        </ETVPopup>

        {/* Grandstream import */}
        <ETVPopup
          open={window === 'grandstream_import_div'}
          onClose={() => (this.state.grandstreamLoading || this.state.grandstreamSaving ? null : this.showWindow('', false))}
          width="small"
          icon={<CloudDownload/>}
          title={this.transText('import_from_grandstream', 'Import from Grandstream')}
          description={this.state.grandstreamStep === 'address'
            ? this.transText('grandstream_enter_ip_description', 'Enter the private LAN IP address of the Grandstream device.')
            : this.transText('grandstream_select_contacts_description', 'Select the contacts to import into Intercom.')}
          loading={this.state.grandstreamLoading || this.state.grandstreamSaving}
          contentSx={{display:'grid',gap:2}}
          bottomActions={this.state.grandstreamStep === 'address'
            ? <><ETVActionButton action="cancel" disabled={this.state.grandstreamLoading} onClick={()=>this.showWindow('',false)}/><ETVActionButton action="update" label={this.transText('next', 'Next')} loading={this.state.grandstreamLoading} disabled={!(this.state.grandstreamIp || '').trim()} onClick={this.previewGrandstreamContacts}/></>
            : <><ETVActionButton action="cancel" label={this.transText('back', 'Back')} disabled={this.state.grandstreamSaving} onClick={()=>this.setState({grandstreamStep:'address',grandstreamError:''})}/><ETVActionButton action="save" loading={this.state.grandstreamSaving} disabled={!this.state.grandstreamContacts.some((contact)=>contact.selected)} onClick={this.saveGrandstreamContacts}/></>}
        >
          {this.state.grandstreamError && <Alert severity="error">{this.state.grandstreamError}</Alert>}
          {this.state.grandstreamStep === 'address' ? (
            <TextField
              autoFocus
              fullWidth
              label={this.transText('grandstream_ip_address', 'Grandstream IP address')}
              placeholder="192.168.1.20"
              value={this.state.grandstreamIp}
              onChange={(event)=>this.setState({grandstreamIp:event.target.value,grandstreamError:''})}
              helperText={this.transText('grandstream_ip_hint', 'The backend must be connected to the same private network as the Grandstream device.')}
              onKeyDown={(event)=>{if(event.key==='Enter') this.previewGrandstreamContacts();}}
            />
          ) : (
            <Stack spacing={1} sx={{maxHeight:420,overflowY:'auto'}}>
              {this.state.grandstreamContacts.map((contact)=>(
                <Box key={contact.importKey} sx={{display:'flex',alignItems:'center',gap:1,p:1,border:1,borderColor:'divider',borderRadius:2}}>
                  <Checkbox checked={!!contact.selected} onChange={()=>this.toggleGrandstreamContact(contact.importKey)}/>
                  <Box sx={{minWidth:0}}>
                    <Typography fontWeight={600}>{contact.name || contact.phone}</Typography>
                    <Typography variant="body2" color="text.secondary">{contact.phone}{contact.type ? ` · ${contact.type}` : ''}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </ETVPopup>

        {/* Change password */}
        <ETVPopup
          open={window === 'change_password_div'}
          onClose={() => (this.state.changePwSaving ? null : this.showWindow('', false))}
          width="small" icon={<Password/>} title={ETVConstants.trans('change_password') || 'Change password'}
          description={this.state.changePwUserName}
          loading={this.state.changePwSaving}
          contentSx={{display:'grid',gap:2}}
          bottomActions={<><ETVActionButton action="cancel" disabled={this.state.changePwSaving} onClick={()=>this.showWindow('',false)}/><ETVActionButton action="update" label="Update password" loading={this.state.changePwSaving} disabled={!canSavePw} onClick={this.submitChangePassword}/></>}
        >
            <TextField
              label={ETVConstants.trans('new_password') || 'New password'}
              fullWidth
              type="password"
              value={this.state.changePwNew}
              onChange={(e) => this.setState({ changePwNew: e.target.value })}
              autoComplete="new-password"
            />

            <TextField
              label={ETVConstants.trans('confirm_password') || 'Confirm password'}
              fullWidth
              type="password"
              value={this.state.changePwConfirm}
              onChange={(e) => this.setState({ changePwConfirm: e.target.value })}
              autoComplete="new-password"
              error={!!this.state.changePwConfirm && this.state.changePwConfirm !== this.state.changePwNew}
              helperText={
                !!this.state.changePwConfirm && this.state.changePwConfirm !== this.state.changePwNew
                  ? ETVConstants.trans('passwords_do_not_match') || 'Passwords do not match'
                  : ' '
              }
            />
        </ETVPopup>

        {/* Edit intercom user */}
        <ETVPopup
          open={window === 'edit_intercom_user_div'}
          onClose={() => (this.state.editUserSaving ? null : this.showWindow('', false))}
          width="small" icon={<Edit/>} title={ETVConstants.trans('edit_intercom_user') || 'Edit intercom user'}
          description={this.state.editUserId ? `${this.state.editUserPrefix}${editUserNameValue}` : ''}
          loading={this.state.editUserSaving} contentSx={{display:'grid',gap:2}}
          bottomActions={<><ETVActionButton action="cancel" disabled={this.state.editUserSaving} onClick={()=>this.showWindow('',false)}/><ETVActionButton action="update" label="Update user" loading={this.state.editUserSaving} disabled={!canSaveEditUser} onClick={this.saveIntercomUserEdit}/></>}
        >
            <FormControl component="fieldset" fullWidth disabled={editUserCloudManaged}>
              <FormLabel component="legend">
                {this.transText('address_type', 'Address type')}
              </FormLabel>
              <RadioGroup
                row
                value={editPrefix}
                onChange={(e) => this.setState({
                  editUserPrefix: e.target.value,
                  editUserName: '',
                  editUserPassword: '',
                  editUserSipExtension: '',
                  editUserSipDomain: '',
                  editUserSipPassword: '',
                  editUserSipTransport: 'udp',
                  showEditUserSipPassword: false,
                })}
              >
                <FormControlLabel value="sip:" control={<Radio />} label={`${this.transText('sip_device', 'SIP')} (sip:)`} />
                <FormControlLabel value="tel:" control={<Radio />} label={`${this.transText('phone_number', 'Tel')} (tel:)`} />
                <FormControlLabel value="app:" control={<Radio />} label={`${this.transText('intercom_app', 'App')} (app:)`} />
              </RadioGroup>
            </FormControl>

            {!editIsSip && (
              <TextField
                label={editIsTel ? (ETVConstants.trans('phone_number') || 'Phone number') : (ETVConstants.trans('app_user_name') || 'App username')}
                fullWidth
                value={this.state.editUserName}
                onChange={(e) => this.setState({ editUserName: e.target.value })}
                error={!!editUserNameError}
                helperText={editUserNameError || (editIsTel ? (ETVConstants.trans('user_name_tel_hint') || 'Enter a Twilio/E.164 phone number, e.g. +6591234567') : (ETVConstants.trans('user_name_hint') || 'Enter the app username without app:'))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{editPrefix}</InputAdornment>,
                }}
              />
            )}

            {editIsSip && (
              <>
                <TextField
                  label={ETVConstants.trans('sip_extension') || 'SIP extension'}
                  fullWidth
                  value={this.state.editUserSipExtension}
                  onChange={(e) => this.setState({ editUserSipExtension: e.target.value })}
                  error={!!editSipError && !editSipExtension}
                  helperText={editUserCloudManaged
                    ? this.transText('cloud_pbx_managed_field', 'Assigned and managed by Cloud PBX')
                    : (editSipError && !editSipExtension ? editSipError : (ETVConstants.trans('sip_extension_hint') || 'PBX extension used to register this SIP user, e.g. 1003'))}
                  disabled={editUserCloudManaged}
                />
                <TextField
                  label={ETVConstants.trans('sip_domain') || 'SIP domain'}
                  fullWidth
                  value={this.state.editUserSipDomain}
                  onChange={(e) => this.setState({ editUserSipDomain: e.target.value })}
                  error={!!editSipError && !!editSipExtension && !editSipDomain}
                  helperText={editSipError && !!editSipExtension && !editSipDomain ? editSipError : (ETVConstants.trans('sip_domain_hint') || 'PBX host or IP address, e.g. 192.168.1.21')}
                  disabled={editUserCloudManaged}
                />
                <FormControl fullWidth>
                  <InputLabel id="edit-user-sip-transport-label">
                    {ETVConstants.trans('sip_transport') || 'SIP transport'}
                  </InputLabel>
                  <Select
                    labelId="edit-user-sip-transport-label"
                    label={ETVConstants.trans('sip_transport') || 'SIP transport'}
                    value={this.state.editUserSipTransport}
                    onChange={(e) => this.setState({ editUserSipTransport: e.target.value })}
                    disabled={editUserCloudManaged}
                  >
                    <MenuItem value="udp">UDP</MenuItem>
                    <MenuItem value="tcp">TCP</MenuItem>
                  </Select>
              </FormControl>
                <TextField
                  label={ETVConstants.trans('sip_password') || 'SIP password'}
                  fullWidth
                  type={this.state.showEditUserSipPassword ? 'text' : 'password'}
                  value={this.state.editUserSipPassword}
                  onChange={(e) => this.setState({ editUserSipPassword: e.target.value })}
                  autoComplete="new-password"
                  error={!!editSipError && !!editSipExtension && !!editSipDomain && !editSipPassword}
                  helperText={editSipError && !!editSipExtension && !!editSipDomain && !editSipPassword ? editSipError : ' '}
                  InputProps={{
                    readOnly: editUserCloudManaged,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => this.setState((prev) => ({ showEditUserSipPassword: !prev.showEditUserSipPassword }))}
                        >
                          {this.state.showEditUserSipPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}

            <TextField
              label={ETVConstants.trans('friendly_name') || 'Friendly name'}
              fullWidth
              value={this.state.editFriendlyName}
              onChange={(e) => this.setState({ editFriendlyName: e.target.value })}
              helperText={this.transText('friendly_name_hint', 'Optional display name, e.g. Reception, Security Room')}
            />
        </ETVPopup>
        {/* DTMF dialog */}
        <IntercomDTMFDialog
            open={window === 'dtmf_dialog_div'}
            onClose={() => this.setState({ window: null })}
        />

        {/* Add intercom user */}
        <ETVPopup
          open={window === 'add_intercom_user_div'}
          onClose={() => (this.state.addSaving ? null : this.showWindow('',false))} width="small"
          icon={isAddingDevice?<Devices/>:isCloudPbxAddition?<CloudQueue/>:<PersonAdd/>} title={addDialogTitle} description={addDialogDesc}
          loading={this.state.addSaving} contentSx={{display:'grid',gap:2}}
          bottomActions={<><ETVActionButton action="cancel" disabled={this.state.addSaving} onClick={()=>this.showWindow('',false)}/><ETVActionButton action="save" label={isAddingDevice?'Link screen':isCloudPbxAddition?'Create and assign number':'Create user'} loading={this.state.addSaving} onClick={()=>this.saveIntercomUser()}/></>}
        >
            {isAddingDevice && (
              <FormControl fullWidth error={showAddValidation && !!deviceRequiredError}>
                <InputLabel id="intercom-device-select-label">
                  {ETVConstants.trans('device') || 'Device'}
                </InputLabel>
                <Select
                  labelId="intercom-device-select-label"
                  id="intercom-device-select"
                  label={this.transText('device', 'Device')}
                  value={newDeviceId}
                  onChange={this.handleSelectDeviceForNewUser}
                >
                  {availableDevices.length === 0 && (
                    <MenuItem value="" disabled>
                      {ETVConstants.trans('no_available_devices') || 'No available devices'}
                    </MenuItem>
                  )}
                  {availableDevices.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.device_name || d.id}
                    </MenuItem>
                  ))}
                </Select>
                {showAddValidation && deviceRequiredError && (
                  <FormHelperText>{deviceRequiredError}</FormHelperText>
                )}
              </FormControl>
            )}

            {isAddingDevice && (
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">
                  {this.transText('screen_sip_source', 'SIP configuration')}
                </FormLabel>
                <RadioGroup
                  value={newDeviceSipSource || 'cloud'}
                  onChange={(event) => this.setState({
                    newDeviceSipSource: event.target.value,
                    newCallRouting: directRouting(),
                    newSipExtension: '',
                    newSipDomain: '',
                    newSipBackupDomain: '',
                    newSipTransport: 'udp',
                    newSipBroadcastIp: '',
                    newSipTestExtension: '',
                    newPassword: '',
                    showNewSipPassword: false,
                    addSaveAttempted: false,
                  })}
                >
                  <FormControlLabel
                    value="cloud"
                    control={<Radio />}
                    label={this.transText('get_sip_from_cloud', 'Get SIP from Cloud PBX')}
                  />
                  <FormControlLabel
                    value="manual"
                    control={<Radio />}
                    label={this.transText('enter_sip_manually', 'Enter SIP manually')}
                  />
                </RadioGroup>
                <FormHelperText>
                  {deviceUsesCloudSip
                    ? this.transText(
                      'get_sip_from_cloud_hint',
                      'The SIP extension, domain, transport and password will be allocated automatically.'
                    )
                    : this.transText(
                      'enter_sip_manually_hint',
                      'Use SIP credentials supplied by your on-premise or third-party PBX.'
                    )}
                </FormHelperText>
              </FormControl>
            )}

            {isAddingDevice && <IntercomCallRoutingFields value={this.state.newCallRouting}
              cloud={deviceUsesCloudSip} onChange={value => this.setState({ newCallRouting: value })} />}
            {!isSipUser && !isCloudPbxAddition && (
              <TextField
                label={
                  isAddingDevice
                    ? (ETVConstants.trans('app_name') || 'App name')
                    : isTelUser
                    ? (ETVConstants.trans('phone_number') || 'Phone number')
                    : (ETVConstants.trans('app_user_name') || 'App username')
                }
                fullWidth
                value={newUserName}
                onChange={(e) => this.setState({ newUserName: e.target.value })}
                error={showAddValidation && !!userNameError}
                helperText={
                  (showAddValidation && userNameError) ||
                  (
                    isAddingDevice
                      ? (ETVConstants.trans('app_name_hint') || 'Intercom app login name. It will be saved as app:name.')
                      : isTelUser
                      ? (ETVConstants.trans('user_name_tel_hint') || 'Enter a Twilio/E.164 phone number, e.g. +6591234567')
                      : (ETVConstants.trans('user_name_hint') || 'Enter the app username without app:')
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {isAddingDevice ? 'app:' : effectivePrefix}
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {showSipFields && (
              <>
                <TextField
                  label={ETVConstants.trans('sip_extension') || 'SIP extension'}
                  fullWidth
                  value={this.state.newSipExtension}
                  onChange={(e) => this.setState({ newSipExtension: e.target.value })}
                  error={showAddValidation && !!sipExtensionError}
                  helperText={(showAddValidation && sipExtensionError) || (ETVConstants.trans('sip_extension_hint') || 'PBX extension used to register this device, e.g. 1002')}
                />
                <TextField
                  label={ETVConstants.trans('sip_domain') || 'SIP domain'}
                  fullWidth
                  value={this.state.newSipDomain}
                  onChange={(e) => this.setState({ newSipDomain: e.target.value })}
                  error={showAddValidation && !!sipDomainError}
                  helperText={(showAddValidation && sipDomainError) || (ETVConstants.trans('sip_domain_hint') || 'PBX host or IP address, e.g. 192.168.1.21')}
                />
                <TextField
                  label={ETVConstants.trans('sip_backup_domain') || 'SIP backup domain'}
                  fullWidth
                  value={this.state.newSipBackupDomain}
                  onChange={(e) => this.setState({ newSipBackupDomain: e.target.value })}
                  error={showAddValidation && !!sipBackupDomainError}
                  helperText={(showAddValidation && sipBackupDomainError) || (ETVConstants.trans('sip_backup_domain_hint') || 'Optional fallback PBX host or IP address')}
                />
                <FormControl fullWidth>
                  <InputLabel id="add-sip-transport-label">
                    {ETVConstants.trans('sip_transport') || 'SIP transport'}
                  </InputLabel>
                  <Select
                    labelId="add-sip-transport-label"
                    label={ETVConstants.trans('sip_transport') || 'SIP transport'}
                    value={newSipTransport}
                    onChange={(e) => this.setState({ newSipTransport: e.target.value })}
                  >
                    <MenuItem value="udp">UDP</MenuItem>
                    <MenuItem value="tcp">TCP</MenuItem>
                  </Select>
              </FormControl>
                <TextField
                  label={ETVConstants.trans('sip_password') || 'SIP password'}
                  fullWidth
                  type={this.state.showNewSipPassword ? 'text' : 'password'}
                  value={this.state.newPassword}
                  onChange={(e) => this.setState({ newPassword: e.target.value })}
                  autoComplete="new-password"
                  error={showAddValidation && !!passwordError}
                  helperText={(showAddValidation && passwordError) || ' '}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => this.setState((prev) => ({ showNewSipPassword: !prev.showNewSipPassword }))}
                        >
                          {this.state.showNewSipPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {isAddingDevice && (
                  <TextField
                    label={ETVConstants.trans('sip_test_extension') || 'SIP test extension'}
                    fullWidth
                    value={this.state.newSipTestExtension}
                    onChange={(e) => this.setState({ newSipTestExtension: e.target.value })}
                    error={showAddValidation && !!sipTestExtensionError}
                    helperText={(showAddValidation && sipTestExtensionError) || (ETVConstants.trans('sip_test_extension_hint') || 'Optional PBX extension for silent test calls, e.g. 9999')}
                  />
                )}
                {isAddingDevice && (
                  <TextField
                    label={ETVConstants.trans('sip_broadcast_ip') || 'SIP broadcast IP'}
                    fullWidth
                    value={this.state.newSipBroadcastIp}
                    onChange={(e) => this.setState({ newSipBroadcastIp: e.target.value })}
                    error={showAddValidation && !!sipBroadcastIpError}
                    helperText={(showAddValidation && sipBroadcastIpError) || (ETVConstants.trans('sip_broadcast_ip_hint') || 'Optional SIP broadcast IP address, e.g. 192.168.1.255')}
                  />
                )}
              </>
            )}

            {isAddingDevice && (
              <Button variant="outlined" onClick={() => this.setState({ alarmConfigurationTarget: 'new' })}>Alarm configuration</Button>
            )}

            {!isAddingDevice && (
              <>
                <TextField
                  label={isCloudPbxAddition
                    ? this.transText('sip_device_name', 'SIP device name')
                    : (ETVConstants.trans('friendly_name') || 'Friendly name')}
                  fullWidth
                  value={this.state.newFriendlyName}
                  onChange={(e) => this.setState({ newFriendlyName: e.target.value })}
                  error={showAddValidation && !!cloudFriendlyNameError}
                  helperText={
                    (showAddValidation && cloudFriendlyNameError) || (isCloudPbxAddition
                      ? this.transText('sip_device_name_hint', 'For example: Reception video phone or Security MicroSIP')
                      : this.transText('friendly_name_hint', 'Optional display name, e.g. Thomas, Reception, Security'))
                  }
                />
                {showVisiblePassword && !isSipUser && (
                  <TextField
                    label={ETVConstants.trans('password') || 'Password'}
                    fullWidth
                    type="password"
                    value={this.state.newPassword}
                    onChange={(e) => this.setState({ newPassword: e.target.value })}
                    autoComplete="new-password"
                    error={showAddValidation && !!passwordError}
                    helperText={(showAddValidation && passwordError) || ' '}
                  />
                )}
              </>
            )}
        </ETVPopup>

        {/* Edit device SIP settings */}
        <ETVPopup
          open={window === 'edit_intercom_device_div'}
          onClose={() => (this.state.editSipSaving ? null : this.showWindow('', false))}
          width="small" icon={<Edit/>} title={ETVConstants.trans('edit_device_settings') || 'Edit device settings'}
          description={this.state.editDeviceName} loading={this.state.editSipSaving}
          contentSx={{display:'grid',gap:2}}
          bottomActions={<><ETVActionButton action="cancel" disabled={this.state.editSipSaving} onClick={()=>this.showWindow('',false)}/><ETVActionButton action="update" label="Update device" loading={this.state.editSipSaving} onClick={this.saveDeviceSipSettings}/></>}
        >
            {window === 'edit_intercom_device_div' && <IntercomCallRoutingFields value={this.state.editCallRouting}
              cloud={editDeviceCloudManaged} screenID={this.state.editDeviceUserId}
              onChange={value => this.setState({ editCallRouting: value })} />}
            <TextField
              label="Caller display name"
              fullWidth
              value={this.state.editDeviceCallerName}
              onChange={(e) => this.setState({ editDeviceCallerName: e.target.value })}
              inputProps={{ maxLength: 100 }}
              helperText="Name shown to the person receiving the call, e.g. Building A – Lift 1. Leave blank to show the SIP number."
            />
            <TextField
              label={ETVConstants.trans('sip_extension') || 'SIP extension'}
              fullWidth
              value={this.state.editSipExtension}
              onChange={(e) => this.setState({ editSipExtension: e.target.value })}
              helperText={editDeviceCloudManaged
                ? this.transText('cloud_pbx_managed_field', 'Assigned and managed by Cloud PBX')
                : (ETVConstants.trans('sip_extension_hint') || 'PBX extension used to register this device, e.g. 1002')}
              InputProps={{ readOnly: editDeviceCloudManaged }}
            />
            <TextField
              label={ETVConstants.trans('sip_domain') || 'SIP domain'}
              fullWidth
              value={this.state.editSipDomain}
              onChange={(e) => this.setState({ editSipDomain: e.target.value })}
              helperText={ETVConstants.trans('sip_domain_hint') || 'PBX host or IP address, e.g. 192.168.1.21'}
              InputProps={{ readOnly: editDeviceCloudManaged }}
            />
            <TextField
              label={ETVConstants.trans('sip_backup_domain') || 'SIP backup domain'}
              fullWidth
              value={this.state.editSipBackupDomain}
              onChange={(e) => this.setState({ editSipBackupDomain: e.target.value })}
              error={!!editDeviceSipBackupDomainError}
              helperText={editDeviceSipBackupDomainError || ETVConstants.trans('sip_backup_domain_hint') || 'Optional fallback PBX host or IP address'}
            />
            <FormControl fullWidth>
              <InputLabel id="edit-sip-transport-label">
                {ETVConstants.trans('sip_transport') || 'SIP transport'}
              </InputLabel>
              <Select
                labelId="edit-sip-transport-label"
                label={ETVConstants.trans('sip_transport') || 'SIP transport'}
                value={this.state.editSipTransport}
                onChange={(e) => this.setState({ editSipTransport: e.target.value })}
                disabled={editDeviceCloudManaged}
              >
                <MenuItem value="udp">UDP</MenuItem>
                <MenuItem value="tcp">TCP</MenuItem>
              </Select>
              </FormControl>
            {editDeviceCloudManaged ? (
              <Alert severity="info">
                {this.transText('cloud_pbx_screen_password_hidden', 'The Cloud PBX screen password is managed automatically and is not displayed.')}
              </Alert>
            ) : (
              <TextField
                label={ETVConstants.trans('sip_password') || 'SIP password'}
                fullWidth
                type={this.state.showEditSipPassword ? 'text' : 'password'}
                value={this.state.editSipPassword}
                onChange={(e) => this.setState({ editSipPassword: e.target.value })}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => this.setState((prev) => ({ showEditSipPassword: !prev.showEditSipPassword }))}
                      >
                        {this.state.showEditSipPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <TextField
              label={ETVConstants.trans('sip_test_extension') || 'SIP test extension'}
              fullWidth
              value={this.state.editSipTestExtension}
              onChange={(e) => this.setState({ editSipTestExtension: e.target.value })}
              helperText={ETVConstants.trans('sip_test_extension_hint') || 'Optional PBX extension for silent test calls, e.g. 9999'}
            />
            <TextField
              label={ETVConstants.trans('sip_broadcast_ip') || 'SIP broadcast IP'}
              fullWidth
              value={this.state.editSipBroadcastIp}
              onChange={(e) => this.setState({ editSipBroadcastIp: e.target.value })}
              helperText={ETVConstants.trans('sip_broadcast_ip_hint') || 'Optional SIP broadcast IP address, e.g. 192.168.1.255'}
            />
            <Button variant="outlined" onClick={() => this.setState({ alarmConfigurationTarget: 'edit' })}>Alarm configuration</Button>
        </ETVPopup>
        {this.state.alarmConfigurationTarget && <AlarmConfigurationDialog
          value={this.state[`${this.state.alarmConfigurationTarget}AlarmConfiguration`]}
          enabled={this.state[`${this.state.alarmConfigurationTarget}En8128AlarmWorkflowEnabled`]}
          status={this.state.alarmConfigurationTarget === 'edit' ? this.state.editAlarmConfigurationStatus : null}
          onClose={() => this.setState({ alarmConfigurationTarget: null })}
          onApply={(configuration, enabled) => this.setState({
            [`${this.state.alarmConfigurationTarget}AlarmConfiguration`]: configuration,
            [`${this.state.alarmConfigurationTarget}En8128AlarmWorkflowEnabled`]: enabled,
            alarmConfigurationTarget: null,
          })}
        />}
        {/* Assignments for a device */}
        <ETVPopup open={window === 'assign_user_dialog_div'} onClose={()=>this.setState({window:''})} width="standard"
          icon={<Assignment/>} title={ETVConstants.trans('emergency_user') || 'Emergency user'}
          description="Assign emergency contacts and schedules to this elevator screen."
          bottomActions={<ETVActionButton action="cancel" label="Close" onClick={()=>this.setState({window:''})}/> }>
            {selected_device_id ? (
              <IntercomAssignmentDialog
                all_users={users}
                device_id={selected_device_id}
                onClose={() => {
                  this.setState({ window: '' });
                  this.loadIntercomUsers();
                }}
              />
            ) : (
              <span />
            )}
        </ETVPopup>
      </>
    );
  }
}

export default IntercomMgmt;
